'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AutoLeaseCalculator() {
  const t = useTranslations("tools.autoLeaseCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState<'standard' | 'reverse'>('standard');
  const [rateType, setRateType] = useState<'moneyfactor' | 'interestrate'>('moneyfactor');
  const [lastRateType, setLastRateType] = useState<'moneyfactor' | 'interestrate'>('moneyfactor');

  const [inputs, setInputs] = useState({
    autoPrice: '50000',
    monthlyPayment: '600',
    leaseTerm: 36,
    interestRate: '0.00208',
    downPayment: '10000',
    tradeIn: '0',
    salesTax: '7',
    residualValue: '24000',
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const parseNumber = (str: string): number => {
    return parseFloat(str.toString().replace(/,/g, '')) || 0;
  };

  const formatCurrency = (value: number): string => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const formatNumberWithCommas = (value: string): string => {
    const num = value.replace(/,/g, '');
    if (num && !isNaN(parseFloat(num))) {
      return parseFloat(num).toLocaleString('en-US');
    }
    return value;
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs({ ...inputs, [field]: value });
  };

  const handleInputBlur = (field: string) => {
    if (field !== 'interestRate' && field !== 'leaseTerm' && field !== 'salesTax') {
      const formatted = formatNumberWithCommas(inputs[field as keyof typeof inputs].toString());
      setInputs({ ...inputs, [field]: formatted });
    }
  };

  const handleRateTypeChange = (newType: 'moneyfactor' | 'interestrate') => {
    const currentValue = parseNumber(inputs.interestRate);
    let newValue = currentValue;

    if (newType === 'moneyfactor' && lastRateType === 'interestrate') {
      newValue = currentValue / 2400;
    } else if (newType === 'interestrate' && lastRateType === 'moneyfactor') {
      newValue = currentValue * 2400;
    }

    setRateType(newType);
    setLastRateType(newType);
    setInputs({ ...inputs, interestRate: newValue.toFixed(newType === 'moneyfactor' ? 5 : 2) });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const leaseTerm = parseInt(inputs.leaseTerm.toString());
    const downPayment = parseNumber(inputs.downPayment);
    const tradeIn = parseNumber(inputs.tradeIn);
    const salesTaxRate = parseNumber(inputs.salesTax) / 100;
    const residualValue = parseNumber(inputs.residualValue);

    let interestRate = parseNumber(inputs.interestRate);
    if (rateType === 'interestrate') {
      interestRate = interestRate / 2400;
    }

    let autoPrice: number, monthlyPayment: number;
    let calculatedResults: any = {};

    if (currentTab === 'standard') {
      autoPrice = parseNumber(inputs.autoPrice);

      if (autoPrice <= 0 || leaseTerm <= 0 || residualValue < 0) {
        setError(t('errorInvalidNumbers'));
        return;
      }

      if (residualValue >= autoPrice) {
        setError(t('errorResidualTooHigh'));
        return;
      }

      const netCapCost = autoPrice - downPayment - tradeIn;
      const depreciation = netCapCost - residualValue;
      const monthlyDepreciation = depreciation / leaseTerm;
      const monthlyFinanceCharge = (netCapCost + residualValue) * interestRate;
      const basePayment = monthlyDepreciation + monthlyFinanceCharge;
      const monthlySalesTax = basePayment * salesTaxRate;
      monthlyPayment = basePayment + monthlySalesTax;

      const totalPayments = monthlyPayment * leaseTerm;
      const totalDepreciation = depreciation;
      const totalInterest = monthlyFinanceCharge * leaseTerm;
      const totalSalesTax = monthlySalesTax * leaseTerm;
      const totalCost = downPayment + tradeIn + totalPayments;

      calculatedResults = {
        mainLabel: t('monthlyLeasePayment'),
        mainValue: monthlyPayment,
        totalCost,
        depreciation: totalDepreciation,
        totalInterest,
        totalSalesTax,
        breakdown: [
          { label: t('autoPrice'), value: autoPrice },
          { label: t('downPayment'), value: downPayment },
          { label: t('tradeInValue'), value: tradeIn },
          { label: t('netCapitalizedCost'), value: netCapCost },
          { label: t('residualValue'), value: residualValue },
          { label: t('monthlyDepreciation'), value: monthlyDepreciation },
          { label: t('monthlyFinanceCharge'), value: monthlyFinanceCharge },
          { label: t('monthlySalesTax'), value: monthlySalesTax },
          { label: t('monthlyPayment'), value: monthlyPayment, isTotal: true },
          { label: t('totalLeaseCost'), value: totalCost, isTotal: true },
        ],
      };
    } else {
      monthlyPayment = parseNumber(inputs.monthlyPayment);

      if (monthlyPayment <= 0 || leaseTerm <= 0 || residualValue < 0) {
        setError(t('errorInvalidNumbers'));
        return;
      }

      const monthlySalesTax = monthlyPayment * (salesTaxRate / (1 + salesTaxRate));
      const basePayment = monthlyPayment - monthlySalesTax;
      const monthlyFinanceCharge = basePayment / (1 + (1 / ((residualValue / leaseTerm) / interestRate + 1)));
      const monthlyDepreciation = basePayment - monthlyFinanceCharge;
      const depreciation = monthlyDepreciation * leaseTerm;
      const netCapCost = depreciation + residualValue;
      autoPrice = netCapCost + downPayment + tradeIn;

      const totalPayments = monthlyPayment * leaseTerm;
      const totalInterest = monthlyFinanceCharge * leaseTerm;
      const totalSalesTax = monthlySalesTax * leaseTerm;
      const totalCost = downPayment + tradeIn + totalPayments;

      calculatedResults = {
        mainLabel: t('estimatedAutoPrice'),
        mainValue: autoPrice,
        totalCost,
        depreciation,
        totalInterest,
        totalSalesTax,
        breakdown: [
          { label: t('monthlyPayment'), value: monthlyPayment },
          { label: t('leaseTerm'), value: `${leaseTerm} ${t('months')}`, isText: true },
          { label: t('downPayment'), value: downPayment },
          { label: t('tradeInValue'), value: tradeIn },
          { label: t('residualValue'), value: residualValue },
          { label: t('totalDepreciation'), value: depreciation },
          { label: t('totalInterest'), value: totalInterest },
          { label: t('totalSalesTax'), value: totalSalesTax },
          { label: t('estimatedAutoPrice'), value: autoPrice, isTotal: true },
          { label: t('totalLeaseCost'), value: totalCost, isTotal: true },
        ],
      };
    }

    setResults(calculatedResults);
    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      { background: 'white' },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formTabs: getResponsiveStyle(
      {
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #e5e7eb',
      },
      { flexDirection: 'column' as const, gap: '0.5rem' },
      { flexDirection: 'row' as const, gap: '1rem' },
      { flexDirection: 'row' as const, gap: '1rem' }
    ),
    tabBtn: (isActive: boolean) => getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        background: 'none',
        border: 'none',
        borderBottom: '3px solid transparent',
        color: isActive ? '#2563eb' : '#6b7280',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s',
        ...(isActive && { borderBottomColor: '#2563eb' }),
      },
      {
        borderBottom: 'none',
        borderLeft: '3px solid transparent',
        textAlign: 'left' as const,
        ...(isActive && { borderLeftColor: '#2563eb', borderBottomColor: 'transparent' }),
      },
      {},
      {}
    ),
    formGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1.5rem',
        marginBottom: '2rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      {
        fontWeight: 600,
        color: '#374151',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    tooltip: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '18px',
      height: '18px',
      background: '#6b7280',
      color: 'white',
      borderRadius: '50%',
      fontSize: '0.75rem',
      cursor: 'help',
      position: 'relative' as const,
    },
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        background: 'white',
        width: '100%',
      },
      {},
      {},
      {}
    ),
    select: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        background: 'white',
        width: '100%',
        cursor: 'pointer',
      },
      {},
      {},
      {}
    ),
    inputWithPrefix: {
      position: 'relative' as const,
    },
    inputPrefix: {
      position: 'absolute' as const,
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontWeight: 600,
    },
    inputWithPrefixInput: getResponsiveStyle(
      {
        paddingLeft: '2rem',
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        background: 'white',
        width: '100%',
      },
      {},
      {},
      {}
    ),
    inputSuffix: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontWeight: 600,
    },
    inputWithSuffixInput: getResponsiveStyle(
      {
        paddingRight: '4rem',
        padding: '0.75rem',
        border: '2px solid #e5e7eb',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s',
        background: 'white',
        width: '100%',
      },
      {},
      {},
      {}
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
      { padding: '1rem', fontSize: '1.1rem', borderRadius: '0.5rem' }
    ),
    errorMessage: getResponsiveStyle(
      {
        background: '#fef2f2',
        border: '2px solid #fecaca',
        color: '#991b1b',
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        display: error ? 'block' : 'none',
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderTop: '3px solid #10b981',
        display: results ? 'block' : 'none',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      {
        margin: '0 0 1.5rem 0',
        color: '#065f46',
        textAlign: 'center' as const,
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
        textAlign: 'center' as const,
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultMainLabel: getResponsiveStyle(
      {
        color: '#6b7280',
        marginBottom: '0.5rem',
      },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    resultMainValue: getResponsiveStyle(
      {
        fontWeight: 800,
        color: '#2563eb',
        margin: 0,
      },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '2.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginBottom: '1.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.25rem' }
    ),
    resultLabel: getResponsiveStyle(
      {
        display: 'block',
        color: '#6b7280',
        marginBottom: '0.5rem',
        fontWeight: 600,
      },
      { fontSize: '0.8rem' },
      { fontSize: '0.85rem' },
      { fontSize: '0.85rem' }
    ),
    resultValue: getResponsiveStyle(
      {
        display: 'block',
        fontWeight: 700,
        color: '#1f2937',
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    breakdownTable: {
      background: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    th: getResponsiveStyle(
      {
        background: '#f9fafb',
        fontWeight: 700,
        color: '#374151',
        textAlign: 'left' as const,
        borderBottom: '1px solid #e5e7eb',
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    td: getResponsiveStyle(
      {
        textAlign: 'left' as const,
        borderBottom: '1px solid #e5e7eb',
      },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    totalRow: {
      background: '#f0f9ff',
      fontWeight: 700,
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '1rem',
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.75rem',
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      {
        lineHeight: 1.6,
        color: colors.textSecondary,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    list: getResponsiveStyle(
      {
        lineHeight: 1.8,
        color: colors.textSecondary,
        paddingLeft: '1.5rem',
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.5rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    reference: {
      marginBottom: '0.5rem',
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.toolHeaderTitle}>🚗 {t('toolTitle')}</h2>
        </div>

        <div style={styles.calculatorForm}>
          <div style={styles.formTabs}>
            <button
              type="button"
              style={styles.tabBtn(currentTab === 'standard')}
              onClick={() => setCurrentTab('standard')}
            >
              {t('tabStandard')}
            </button>
            <button
              type="button"
              style={styles.tabBtn(currentTab === 'reverse')}
              onClick={() => setCurrentTab('reverse')}
            >
              {t('tabReverse')}
            </button>
          </div>

          <div style={styles.errorMessage}>
            {error}
          </div>

          <form onSubmit={handleCalculate}>
            <div style={styles.formGrid}>
              {currentTab === 'standard' ? (
                <div style={styles.formGroup}>
                  <label htmlFor="auto-price" style={styles.label}>
                    {t('autoPriceLabel')}
                  </label>
                  <div style={styles.inputWithPrefix}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="text"
                      id="auto-price"
                      value={inputs.autoPrice}
                      onChange={(e) => handleInputChange('autoPrice', e.target.value)}
                      onBlur={() => handleInputBlur('autoPrice')}
                      style={styles.inputWithPrefixInput}
                      required
                    />
                  </div>
                </div>
              ) : (
                <div style={styles.formGroup}>
                  <label htmlFor="monthly-payment" style={styles.label}>
                    {t('monthlyPaymentLabel')}
                  </label>
                  <div style={styles.inputWithPrefix}>
                    <span style={styles.inputPrefix}>$</span>
                    <input
                      type="text"
                      id="monthly-payment"
                      value={inputs.monthlyPayment}
                      onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                      onBlur={() => handleInputBlur('monthlyPayment')}
                      style={styles.inputWithPrefixInput}
                      required
                    />
                  </div>
                </div>
              )}

              <div style={styles.formGroup}>
                <label htmlFor="lease-term" style={styles.label}>
                  {t('leaseTermLabel')}
                  <span style={styles.tooltip} title={t('leaseTermTooltip')}>?</span>
                </label>
                <div style={styles.inputWithPrefix}>
                  <input
                    type="number"
                    id="lease-term"
                    value={inputs.leaseTerm}
                    onChange={(e) => handleInputChange('leaseTerm', e.target.value)}
                    style={styles.inputWithSuffixInput}
                    min="1"
                    max="120"
                    required
                  />
                  <span style={styles.inputSuffix}>{t('months')}</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="rate-type" style={styles.label}>
                  {t('interestRateTypeLabel')}
                </label>
                <select
                  id="rate-type"
                  value={rateType}
                  onChange={(e) => handleRateTypeChange(e.target.value as 'moneyfactor' | 'interestrate')}
                  style={styles.select}
                >
                  <option value="moneyfactor">{t('moneyFactor')}</option>
                  <option value="interestrate">{t('interestRatePercent')}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="interest-rate" style={styles.label}>
                  {rateType === 'moneyfactor' ? t('moneyFactorLabel') : t('interestRateLabel')}
                  <span style={styles.tooltip} title={rateType === 'moneyfactor' ? t('moneyFactorTooltip') : t('interestRateTooltip')}>?</span>
                </label>
                <input
                  type="text"
                  id="interest-rate"
                  value={inputs.interestRate}
                  onChange={(e) => handleInputChange('interestRate', e.target.value)}
                  style={styles.input}
                  step="0.00001"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="down-payment" style={styles.label}>
                  {t('downPaymentLabel')}
                </label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="text"
                    id="down-payment"
                    value={inputs.downPayment}
                    onChange={(e) => handleInputChange('downPayment', e.target.value)}
                    onBlur={() => handleInputBlur('downPayment')}
                    style={styles.inputWithPrefixInput}
                    required
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="trade-in" style={styles.label}>
                  {t('tradeInLabel')}
                  <span style={styles.tooltip} title={t('tradeInTooltip')}>?</span>
                </label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="text"
                    id="trade-in"
                    value={inputs.tradeIn}
                    onChange={(e) => handleInputChange('tradeIn', e.target.value)}
                    onBlur={() => handleInputBlur('tradeIn')}
                    style={styles.inputWithPrefixInput}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="sales-tax" style={styles.label}>
                  {t('salesTaxLabel')}
                  <span style={styles.tooltip} title={t('salesTaxTooltip')}>?</span>
                </label>
                <div style={styles.inputWithPrefix}>
                  <input
                    type="text"
                    id="sales-tax"
                    value={inputs.salesTax}
                    onChange={(e) => handleInputChange('salesTax', e.target.value)}
                    style={styles.inputWithSuffixInput}
                    required
                  />
                  <span style={styles.inputSuffix}>%</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="residual-value" style={styles.label}>
                  {t('residualValueLabel')}
                  <span style={styles.tooltip} title={t('residualValueTooltip')}>?</span>
                </label>
                <div style={styles.inputWithPrefix}>
                  <span style={styles.inputPrefix}>$</span>
                  <input
                    type="text"
                    id="residual-value"
                    value={inputs.residualValue}
                    onChange={(e) => handleInputChange('residualValue', e.target.value)}
                    onBlur={() => handleInputBlur('residualValue')}
                    style={styles.inputWithPrefixInput}
                    required
                  />
                </div>
              </div>
            </div>

            <button type="submit" style={styles.calculateBtn}>
              {t('calculateButton')}
            </button>
          </form>
        </div>

        {results && (
          <div style={styles.results} id="results">
            <h3 style={styles.resultsTitle}>📊 {t('resultsTitle')}</h3>

            <div style={styles.resultMain}>
              <div style={styles.resultMainLabel}>{results.mainLabel}</div>
              <div style={styles.resultMainValue}>{formatCurrency(results.mainValue)}</div>
            </div>

            <div style={styles.resultGrid}>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalLeaseCost')}</span>
                <span style={styles.resultValue}>{formatCurrency(results.totalCost)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalDepreciation')}</span>
                <span style={styles.resultValue}>{formatCurrency(results.depreciation)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalInterest')}</span>
                <span style={styles.resultValue}>{formatCurrency(results.totalInterest)}</span>
              </div>
              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t('totalSalesTax')}</span>
                <span style={styles.resultValue}>{formatCurrency(results.totalSalesTax)}</span>
              </div>
            </div>

            <div style={styles.breakdownTable}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t('costComponent')}</th>
                    <th style={styles.th}>{t('amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.breakdown.map((item: any, index: number) => (
                    <tr key={index} style={item.isTotal ? styles.totalRow : {}}>
                      <td style={styles.td}>{item.isTotal ? <strong>{item.label}</strong> : item.label}</td>
                      <td style={styles.td}>
                        {item.isTotal ? (
                          <strong>{item.isText ? item.value : formatCurrency(item.value)}</strong>
                        ) : (
                          item.isText ? item.value : formatCurrency(item.value)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <h3 style={styles.h3}>{t('insightsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <h3 style={styles.h3}>{t('insightsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <h3 style={styles.h3}>{t('insightsSubtitle3')}</h3>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={styles.h3}>{t('detailsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <h3 style={styles.h3}>{t('detailsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
        <h3 style={styles.h3}>{t('detailsSubtitle3')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsBullet1')}</li>
          <li style={styles.listItem}>{t('detailsBullet2')}</li>
          <li style={styles.listItem}>{t('detailsBullet3')}</li>
          <li style={styles.listItem}>{t('detailsBullet4')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faqQuestion1')}</h3>
          <p style={styles.paragraph}>{t('faqAnswer1')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faqQuestion2')}</h3>
          <p style={styles.paragraph}>{t('faqAnswer2')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faqQuestion3')}</h3>
          <p style={styles.paragraph}>{t('faqAnswer3')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faqQuestion4')}</h3>
          <p style={styles.paragraph}>{t('faqAnswer4')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.h3}>{t('faqQuestion5')}</h3>
          <p style={styles.paragraph}>{t('faqAnswer5')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.reference}>
            <a href="https://www.nerdwallet.com/auto-loans/learn/nerdwallet-lease-calculator" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t('reference1')}
            </a>
          </li>
          <li style={styles.reference}>
            <a href="https://www.edmunds.com/calculators/car-lease.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t('reference2')}
            </a>
          </li>
          <li style={styles.reference}>
            <a href="https://www.kbb.com/car-lease-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t('reference3')}
            </a>
          </li>
          <li style={styles.reference}>
            <a href="https://www.bankrate.com/loans/auto-loans/lease-vs-buy-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t('reference4')}
            </a>
          </li>
          <li style={styles.reference}>
            <a href="https://www.calculator.net/lease-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}